import { useState } from 'react';
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Target, Zap } from 'lucide-react';

export interface GoalsStepData {
 communicationStyle?: string;
 timeline?: string;
 targetTimeline?: string;
 focusAreas?: string[];
 careerGoals?: { description: string }[];
 interviewObjectives?: string[];
 customGoals?: string;
 completedAt?: string;
}

interface GoalsPreferencesStepProps {
 initialData?: GoalsStepData;
 onDataUpdate: (data: GoalsStepData) => Promise<void>;
 onNext: () => void;
 onPrevious: () => void;
}

export function GoalsPreferencesStep({
 initialData,
 onDataUpdate,
 onNext,
 onPrevious,
}: GoalsPreferencesStepProps) {
 const [loading, setLoading] = useState(false);
 const [communicationStyle, setCommunicationStyle] = useState(
  initialData?.communicationStyle || 'professional'
 );
 const [timeline, setTimeline] = useState(initialData?.timeline || 'immediate');
 const [focusAreas, setFocusAreas] = useState<string[]>(
  initialData?.focusAreas || []
 );
 const [careerGoals, setCareerGoals] = useState<string>(
  initialData?.careerGoals?.map((g: any) => g.description).join(', ') || ''
 );
 const [interviewObjectives, setInterviewObjectives] = useState<string>(
  initialData?.interviewObjectives?.join(', ') || ''
 );
 const [customGoals, setCustomGoals] = useState(initialData?.customGoals || '');

 const toggleFocusArea = (area: string) => {
  if (focusAreas.includes(area)) {
   setFocusAreas(focusAreas.filter((a) => a !== area));
  } else {
   setFocusAreas([...focusAreas, area]);
  }
 };

 const handleComplete = async () => {
  setLoading(true);
  try {
   await onDataUpdate({
    communicationStyle,
    targetTimeline: timeline,
    focusAreas,
    careerGoals: careerGoals
     .split(',')
     .map((s) => ({ description: s.trim() }))
     .filter((g) => g.description),
    interviewObjectives: interviewObjectives
     .split(',')
     .map((s) => s.trim())
     .filter((s) => s),
    customGoals,
    completedAt: new Date().toISOString(),
   });
   onNext();
  } catch (error) {
   console.error(error);
  } finally {
   setLoading(false);
  }
 };

 const focusOptions = [
  'System Design',
  'Behavioral',
  'Frontend Architecture',
  'Database Design',
  'Cloud Infrastructure',
  'Salary Negotiations',
  'Leadership & Mentoring',
  'Remote Work Best Practices',
  'Team Conflict Resolution',
  'Technical Knowledge',
  'Problem Solving (Theoretical)',
 ];

 return (
  <div className='max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500'>
   <div className='grid md:grid-cols-3 gap-6'>
    {/* Left Column: Input Form */}
    <div className='md:col-span-2 space-y-6'>
     <Card className='rounded-none border-border bg-black/40 backdrop-blur-sm'>
      <CardHeader>
       <CardTitle className='font-mono uppercase tracking-wider text-xl flex items-center gap-2'>
        <Target className='w-5 h-5 text-primary' />
        Target_Parameters
       </CardTitle>
       <CardDescription className='font-mono text-xs'>
        Define your success criteria and optimization goals.
       </CardDescription>
      </CardHeader>

      <CardContent className='space-y-8'>
       {/* Communication Style (AI Tone) */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         AI Persona / Communication Style
        </Label>
        <Select
         value={communicationStyle}
         onValueChange={setCommunicationStyle}
        >
         <SelectTrigger className='rounded-none font-mono bg-secondary/5 border-border'>
          <SelectValue placeholder='Select AI Tone' />
         </SelectTrigger>
         <SelectContent className='rounded-none font-mono border-border bg-background'>
          <SelectItem value='professional'>PROFESSIONAL (Standard)</SelectItem>
          <SelectItem value='friendly'>FRIENDLY (Coach-like)</SelectItem>
          <SelectItem value='direct'>DIRECT (Concise)</SelectItem>
          <SelectItem value='socratic'>SOCRATIC (Question-based)</SelectItem>
         </SelectContent>
        </Select>
       </div>

       {/* Timeline */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Interview Timeline
        </Label>
        <Select value={timeline} onValueChange={setTimeline}>
         <SelectTrigger className='rounded-none font-mono bg-secondary/5 border-border'>
          <SelectValue placeholder='Select timeline' />
         </SelectTrigger>
         <SelectContent className='rounded-none font-mono border-border bg-background'>
          <SelectItem value='immediate'>IMMEDIATE (&lt; 2 WEEKS)</SelectItem>
          <SelectItem value='short_term'>SHORT_TERM (1 MONTH)</SelectItem>
          <SelectItem value='medium_term'>MEDIUM_TERM (3 MONTHS)</SelectItem>
          <SelectItem value='passive'>PASSIVE_LOOKING</SelectItem>
         </SelectContent>
        </Select>
       </div>

       {/* Focus Areas */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Focus Areas (Select all that apply)
        </Label>
        <div className='grid grid-cols-2 gap-3'>
         {focusOptions.map((option) => (
          <div
           key={option}
           onClick={() => toggleFocusArea(option)}
           className={`
            cursor-pointer border p-3 flex items-start gap-3 transition-colors
            ${
             focusAreas.includes(option)
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-border hover:border-primary/50 text-muted-foreground'
            }
           `}
          >
           <Checkbox
            checked={focusAreas.includes(option)}
            className='rounded-none border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:text-black mt-0.5'
           />
           <span className='font-mono text-xs uppercase'>{option}</span>
          </div>
         ))}
        </div>
       </div>

       {/* Additional Context */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Additional Configurations (Optional)
        </Label>
        <textarea
         value={customGoals}
         onChange={(e) => setCustomGoals(e.target.value)}
         placeholder='e.g. "I want to focus on clean code principles" or "Limit sessions to 30 mins"'
         className='flex min-h-[80px] w-full rounded-none border border-input bg-secondary/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono'
        />
       </div>

       {/* Career Goals */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Career Goals (Comma separated)
        </Label>
        <textarea
         value={careerGoals}
         onChange={(e) => setCareerGoals(e.target.value)}
         placeholder='e.g. "Switch to FinTech, Master System Design, Lead a team"'
         className='flex min-h-[60px] w-full rounded-none border border-input bg-secondary/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-mono'
        />
       </div>

       {/* Interview Objectives */}
       <div className='space-y-4'>
        <Label className='font-mono uppercase text-xs text-muted-foreground'>
         Current Session Objectives (Comma separated)
        </Label>
        <textarea
         value={interviewObjectives}
         onChange={(e) => setInterviewObjectives(e.target.value)}
         placeholder='e.g. "Algorithmic thinking, Behavioral questions, System scalability"'
         className='flex min-h-[60px] w-full rounded-none border border-input bg-secondary/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 font-mono'
        />
       </div>
      </CardContent>
     </Card>
    </div>

    {/* Right Column: Verification/Preview */}
    <div className='space-y-6'>
     <Card className='rounded-none border-border bg-secondary/5 h-full'>
      <CardHeader>
       <CardTitle className='font-mono uppercase tracking-wider text-sm flex items-center gap-2 text-muted-foreground'>
        <Zap className='w-4 h-4' />
        Configuration_Summary
       </CardTitle>
      </CardHeader>
      <CardContent className='font-mono text-xs space-y-6'>
       <div>
        <div className='text-muted-foreground mb-1'>COMMUNICATION_PROTOCOL</div>
        <div className='text-foreground uppercase'>{communicationStyle}</div>
       </div>

       <div>
        <div className='text-muted-foreground mb-1'>URGENCY_LEVEL</div>
        <div
         className={`
         ${
          timeline === 'immediate'
           ? 'text-red-500 animate-pulse font-bold'
           : 'text-foreground'
         }
        `}
        >
         {timeline.toUpperCase()}
        </div>
       </div>

       <div>
        <div className='text-muted-foreground mb-1'>SELECTED_MODULES</div>
        <div className='space-y-1'>
         {focusAreas.length > 0 ? (
          focusAreas.map((area) => (
           <div key={area} className='flex items-center gap-2'>
            <span className='text-primary'>+</span> {area}
           </div>
          ))
         ) : (
          <span className='text-muted-foreground italic'>
           Pending selection...
          </span>
         )}
        </div>
       </div>

       <div className='pt-8'>
        <div className='border border-primary/20 bg-primary/10 p-4 text-primary'>
         <div className='mb-2 font-bold'>SYSTEM_PROJECTION</div>
         <p className='opacity-80 leading-relaxed'>
          Based on configuration, system will generate a customized curriculum
          focusing on
          {focusAreas.length > 0
           ? ` ${focusAreas[0]} and ${focusAreas.length - 1} other areas`
           : ' general competency'}
          .
         </p>
        </div>
       </div>
      </CardContent>
     </Card>
    </div>
   </div>

   {/* Action Buttons */}
   <div className='flex justify-between pt-6 border-t border-border'>
    <Button
     variant='ghost'
     onClick={onPrevious}
     className='font-mono rounded-none hover:bg-transparent hover:text-primary'
    >
     {'< BACK'}
    </Button>
    <Button
     onClick={handleComplete}
     disabled={loading}
     className='rounded-none font-mono uppercase tracking-widest px-8 bg-primary text-black hover:bg-primary/90'
    >
     {loading ? 'INITIALIZING...' : 'INITIALIZE_DASHBOARD >'}
    </Button>
   </div>
  </div>
 );
}
